import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileText, 
  Calendar, 
  Download, 
  Filter, 
  ArrowUpRight, 
  ArrowDownRight, 
  DollarSign,
  FileBarChart,
  AlertCircle,
  Loader2,
  ChevronDown,
  FileDown,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  Settings
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import { useAppStore } from '../store';
import { formatCurrency, formatCurrencyForCSV } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import { supabase } from '../lib/supabase';
import ErrorModal from '../components/modals/ErrorModal';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Define report types
type ReportType = 
  | 'income-statement' 
  | 'income-statement-results'
  | 'accounts-receivable' 
  | 'accounts-payable'
  | 'cash-flow-projection'
  | 'vat-sales'
  | 'vat-purchases'
  | 'sales-by-client'
  | 'purchases-by-supplier'
  | 'express-metrics';

interface ReportData {
  [key: string]: any[];
}

const Reports: React.FC = () => {
  // Get data from store
  const { invoices, payments, expenses, clients, suppliers } = useAppStore();
  
  // State for date range
  const [period, setPeriod] = useState<'current-month' | 'previous-month' | 'custom'>('current-month');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // State for selected report
  const [selectedReport, setSelectedReport] = useState<ReportType>('income-statement');
  
  // State for report data
  const [reportData, setReportData] = useState<ReportData>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  
  // State for category breakdowns
  const [costsByCategory, setCostsByCategory] = useState<Record<string, number>>({});
  const [expensesByCategory, setExpensesByCategory] = useState<Record<string, number>>({});
  
  // State for detailed data
  const [productionDetails, setProductionDetails] = useState<any[]>([]);
  const [sampleDetails, setSampleDetails] = useState<any[]>([]);
  const [expressDetails, setExpressDetails] = useState<any[]>([]);
  const [expressSampleDetails, setExpressSampleDetails] = useState<any[]>([]);
  const [productionCostDetails, setProductionCostDetails] = useState<any[]>([]);
  const [sampleCostDetails, setSampleCostDetails] = useState<any[]>([]);
  const [expressCostDetails, setExpressCostDetails] = useState<any[]>([]);
  const [expressSampleCostDetails, setExpressSampleCostDetails] = useState<any[]>([]);
  
  // State for express cost configuration
  const [expressCostConfig, setExpressCostConfig] = useState({
    hoodieCost: 16000, // Default cost for hoodies
    tshirtCost: 5500  // Default cost for t-shirts
  });
  const [showExpressCostModal, setShowExpressCostModal] = useState(false);
  
  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    productions: false,
    samples: false,
    express: false,
    expressSamples: false,
    productionCosts: false,
    sampleCosts: false,
    expressCosts: false,
    expenses: false
  });
  
  // Get data from store
  const { 
    productions,
    samples,
    express,
    expressSamples,
    expressItems,
    expressSampleItems,
    expenseCategories,
    productionTasks,
    sampleTasks
  } = useAppStore();
  
  // Set default date range based on period
  useEffect(() => {
    const now = new Date();
    let start = new Date();
    let end = new Date();
    
    if (period === 'current-month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (period === 'previous-month') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, [period]);
  
  // Generate report data based on selected report and date range
  const generateReport = async () => {
    if (!startDate || !endDate) {
      setError('Debe seleccionar un rango de fechas');
      setShowErrorModal(true);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      let data: any[] = [];
      
      switch (selectedReport) {
        case 'income-statement':
          data = await generateIncomeStatement();
          break;
        case 'income-statement-results':
          data = await generateIncomeStatementResults();
          break;
        case 'accounts-receivable':
          data = await generateAccountsReceivable();
          break;
        case 'accounts-payable':
          data = await generateAccountsPayable();
          break;
        case 'cash-flow-projection':
          data = await generateCashFlowProjection();
          break;
        case 'vat-sales':
          data = await generateVatSales();
          break;
        case 'vat-purchases':
          data = await generateVatPurchases();
          break;
        case 'sales-by-client':
          data = await generateSalesByClient();
          break;
        case 'purchases-by-supplier':
          data = await generatePurchasesBySupplier();
          break;
        case 'express-metrics':
          data = await generateExpressMetrics();
          break;
        default:
          throw new Error('Tipo de reporte no válido');
      }
      
      setReportData({
        ...reportData,
        [selectedReport]: data
      });
    } catch (error: any) {
      console.error('Error generating report:', error);
      setError(error.message || 'Error al generar el reporte');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Effect to generate report when parameters change
// ⬇️ Reemplazá tu useEffect actual por este
useEffect(() => {
  // Necesitamos fechas válidas Y al menos alguna factura/gasto cargado
  if (!startDate || !endDate) return;
  if (
    invoices.length === 0 &&
    expenses.length === 0 &&
    express.length === 0 &&
    expressSamples.length === 0
  ) return;

  generateReport();         // <- ya es asíncrona; no cambia nada
}, [
  /* depende de… */
  selectedReport,
  startDate,
  endDate,
  invoices,          // 🔑  nuevas dependencias
  expenses,
  express,
  expressSamples
]);

  
  // Income Statement Report
  const generateIncomeStatement = async () => {
    // Get all client payments in the date range (cash inflows)
    const clientPayments = payments.filter(pay => 
      !pay.voided &&
      new Date(pay.date) >= new Date(startDate) && 
      new Date(pay.date) <= new Date(endDate) &&
      (pay.invoice_id ? invoices.find(inv => inv.id === pay.invoice_id)?.entity_type === 'client' : pay.reference?.startsWith('client:'))
    );
    
    // Get all supplier payments in the date range (cash outflows)
    const supplierPayments = payments.filter(pay => 
      !pay.voided &&
      new Date(pay.date) >= new Date(startDate) && 
      new Date(pay.date) <= new Date(endDate) &&
      (pay.invoice_id ? invoices.find(inv => inv.id === pay.invoice_id)?.entity_type === 'supplier' : pay.reference?.startsWith('supplier:'))
    );
    
    // Get all expenses in the date range
    const periodExpenses = expenses.filter(exp => 
      new Date(exp.date) >= new Date(startDate) && 
      new Date(exp.date) <= new Date(endDate)
    );
    
// 1️⃣ ────── CÁLCULO DE INGRESOS DE EFECTIVO ─────────────────────────

// A) pagos de clientes
const clientPaymentsInflow = clientPayments.reduce(
  (s, pay) => s + pay.amount,
  0
);

// B) producciones Express
const expressSales = express
  .filter(o =>
    new Date(o.created_at ?? o.date) >= new Date(startDate) &&
    new Date(o.created_at ?? o.date) <= new Date(endDate)
  )
  .reduce((s, o) => s + (o.express_value ?? o.total ?? 0), 0);

// C) muestras Express
const expressSamplesSales = expressSamples
  .filter(smpl =>
    new Date(smpl.created_at ?? smpl.date) >= new Date(startDate) &&
    new Date(smpl.created_at ?? smpl.date) <= new Date(endDate)
  )
  .reduce((s, smpl) => s + (smpl.sample_value ?? smpl.total ?? 0), 0);

    const sampleSales = samples
  .filter(sample =>
    new Date(sample.created_at) >= new Date(startDate) &&
    new Date(sample.created_at) <= new Date(endDate)
  )
  .reduce((s, sample) => s + (sample.sample_value || 0), 0);
    
// D) empaquetamos
const newSalesBySource = {
  'Pagos de clientes'   : clientPaymentsInflow,
  'Muestras producciones'            : sampleSales,
  'Producciones express': expressSales,
  'Muestras express'    : expressSamplesSales,
};

setSalesBySource(newSalesBySource);

// E) total ingresos de efectivo
const totalCashInflows = Object.values(newSalesBySource).reduce((a, b) => a + b, 0);

// 1️⃣ ──────────────────────────────────────────────────

    const { totalCosts, costsByCategory: costsByCat } = calculateCosts(supplierPayments);
    const { totalExpenses, expensesByCategory: expensesByCat } = calculateExpenses(periodExpenses);
    
    // Calculate net cash flow
    const netCashFlow = totalCashInflows - totalCosts - totalExpenses;
    
    // Update category breakdowns
    setCostsByCategory(costsByCat);
    setExpensesByCategory(expensesByCat);
    
    // Return data in a structured format
    return [
      { category: 'Ingresos de Efectivo', item: 'Total Ingresos', amount: totalCashInflows },
      { category: 'Egresos de Efectivo', item: 'Pagos a Proveedores', amount: -totalCosts },
      { category: 'Flujo Operativo', item: 'Flujo Operativo', amount: totalCashInflows - totalCosts },
      { category: 'Gastos', item: 'Gastos Operativos', amount: -totalExpenses },
      { category: 'Flujo de Efectivo Neto', item: 'Flujo de Efectivo Neto', amount: netCashFlow }
    ];
  };
  
  // Income Statement Results Report
  const generateIncomeStatementResults = async () => {
    // Calculate sales from productions and samples (not invoices)
    const productionSales = productions
      .filter(prod => 
        new Date(prod.created_at) >= new Date(startDate) &&
        new Date(prod.created_at) <= new Date(endDate)
      )
      .map(prod => {
        // Calculate progress from production tasks
        const prodTasks = productionTasks.filter(task => task.production_id === prod.id);
        const totalTasks = prodTasks.length;
        const finishedTasks = prodTasks.filter(task => task.status === 'finished').length;
        const progress = totalTasks > 0 ? Math.round((finishedTasks / totalTasks) * 100) : 0;
        
        return {
          id: prod.id,
          name: prod.name,
          value: prod.production_value || 0,
          progress,
          type: 'production'
        };
      });

    const sampleSales = samples
      .filter(sample =>
        new Date(sample.created_at) >= new Date(startDate) &&
        new Date(sample.created_at) <= new Date(endDate)
      )
      .map(sample => {
        // Calculate progress from sample tasks
        const sampleTasksForSample = sampleTasks.filter(task => task.sample_id === sample.id);
        const totalTasks = sampleTasksForSample.length;
        const finishedTasks = sampleTasksForSample.filter(task => task.status === 'finished').length;
        const progress = totalTasks > 0 ? Math.round((finishedTasks / totalTasks) * 100) : 0;
        
        return {
          id: sample.id,
          name: sample.name,
          value: sample.sample_value || 0,
          progress,
          type: 'sample'
        };
      });

    // Calculate express sales with hoodies and tshirts from items
    const expressSales = express
      .filter(o =>
        new Date(o.created_at ?? o.date) >= new Date(startDate) &&
        new Date(o.created_at ?? o.date) <= new Date(endDate)
      )
      .map(o => {
        // Get items for this express order
        const orderItems = expressItems.filter(item => item.express_id === o.id);
        
        // Calculate hoodies and tshirts
        const hoodies = orderItems
          .filter(item => item.product_type === 'Buzo')
          .reduce((sum, item) => sum + item.quantity, 0);
        
        const tshirts = orderItems
          .filter(item => item.product_type === 'Remera')
          .reduce((sum, item) => sum + item.quantity, 0);
        
        return {
          id: o.id,
          name: o.name,
          value: o.express_value ?? o.total ?? 0,
          hoodies,
          tshirts,
          type: 'express'
        };
      });

    const expressSamplesSales = expressSamples
      .filter(smpl =>
        new Date(smpl.created_at ?? smpl.date) >= new Date(startDate) &&
        new Date(smpl.created_at ?? smpl.date) <= new Date(endDate)
      )
      .map(smpl => {
        // Get items for this express sample
        const sampleItems = expressSampleItems.filter(item => item.express_sample_id === smpl.id);
        
        // Calculate hoodies and tshirts
        const hoodies = sampleItems
          .filter(item => item.product_type === 'Buzo')
          .reduce((sum, item) => sum + item.quantity, 0);
        
        const tshirts = sampleItems
          .filter(item => item.product_type === 'Remera')
          .reduce((sum, item) => sum + item.quantity, 0);
        
        return {
          id: smpl.id,
          name: smpl.name,
          value: smpl.sample_value ?? smpl.total ?? 0,
          hoodies,
          tshirts,
          type: 'express_sample'
        };
      });

    const totalSales = productionSales.reduce((sum, p) => sum + p.value, 0) +
                      sampleSales.reduce((sum, s) => sum + s.value, 0) +
                      expressSales.reduce((sum, e) => sum + e.value, 0) +
                      expressSamplesSales.reduce((sum, es) => sum + es.value, 0);

    // Calculate costs from production manager tasks for productions with start_date in the period
    const productionCosts = productionTasks
      .filter(task => {
        const production = productions.find(prod => prod.id === task.production_id);
        if (!production) return false;
        const d = (production as { start_date?: string }).start_date ?? production.created_at?.slice(0, 10) ?? '';
        const dStr = typeof d === 'string' ? d.slice(0, 10) : '';
        return dStr >= startDate && dStr <= endDate;
      })
      .map(task => {
        const production = productions.find(prod => prod.id === task.production_id);
        const supplier = suppliers.find(s => s.id === task.supplier_id);
        return {
          productionId: task.production_id,
          productionName: production?.name || 'Producción desconocida',
          supplierName: supplier?.name || 'Proveedor desconocido',
          supplierCategory: supplier?.category || 'Sin categoría',
          cost: task.cost || 0,
          type: 'production'
        };
      });

    const sampleCosts = sampleTasks
      .filter(task => {
        const sample = samples.find(s => s.id === task.sample_id);
        if (!sample) return false;
        const d = (sample as { start_date?: string }).start_date ?? sample.created_at?.slice(0, 10) ?? '';
        const dStr = typeof d === 'string' ? d.slice(0, 10) : '';
        return dStr >= startDate && dStr <= endDate;
      })
      .map(task => {
        const sample = samples.find(s => s.id === task.sample_id);
        const supplier = suppliers.find(s => s.id === task.supplier_id);
        return {
          sampleId: task.sample_id,
          sampleName: sample?.name || 'Muestra desconocida',
          supplierName: supplier?.name || 'Proveedor desconocido',
          supplierCategory: supplier?.category || 'Sin categoría',
          cost: task.cost || 0,
          type: 'sample'
        };
      });

    // Calculate express costs based on configured costs
    const expressCosts = expressSales.map(exp => {
      const hoodieCost = (exp.hoodies || 0) * (expressCostConfig.hoodieCost || 0);
      const tshirtCost = (exp.tshirts || 0) * (expressCostConfig.tshirtCost || 0);
      return {
        id: exp.id,
        name: exp.name,
        hoodies: exp.hoodies || 0,
        tshirts: exp.tshirts || 0,
        hoodieCost,
        tshirtCost,
        totalCost: hoodieCost + tshirtCost,
        type: 'express'
      };
    });

    const expressSampleCosts = expressSamplesSales.map(es => {
      const hoodieCost = (es.hoodies || 0) * (expressCostConfig.hoodieCost || 0);
      const tshirtCost = (es.tshirts || 0) * (expressCostConfig.tshirtCost || 0);
      return {
        id: es.id,
        name: es.name,
        hoodies: es.hoodies || 0,
        tshirts: es.tshirts || 0,
        hoodieCost,
        tshirtCost,
        totalCost: hoodieCost + tshirtCost,
        type: 'express_sample'
      };
    });

    const totalProductionCosts = productionCosts.reduce((sum, c) => sum + c.cost, 0);
    const totalSampleCosts = sampleCosts.reduce((sum, c) => sum + c.cost, 0);
    const totalExpressCosts = expressCosts.reduce((sum, c) => sum + c.totalCost, 0);
    const totalExpressSampleCosts = expressSampleCosts.reduce((sum, c) => sum + c.totalCost, 0);
    const totalCosts = totalProductionCosts + totalSampleCosts + totalExpressCosts + totalExpressSampleCosts;

    // Calculate expenses for the period
    const periodExpenses = expenses
      .filter(exp => 
        new Date(exp.date) >= new Date(startDate) && 
        new Date(exp.date) <= new Date(endDate)
      )
      .reduce((sum, exp) => sum + exp.amount, 0);

    // Calculate expenses by category
    const expensesByCategory: Record<string, number> = {};
    expenses
      .filter(exp => 
        new Date(exp.date) >= new Date(startDate) && 
        new Date(exp.date) <= new Date(endDate)
      )
      .forEach(expense => {
        const category = expenseCategories.find(c => c.id === expense.category_id)?.name || 'Sin categoría';
        if (!expensesByCategory[category]) {
          expensesByCategory[category] = 0;
        }
        expensesByCategory[category] += expense.amount;
      });

    const grossProfit = totalSales - totalCosts;
    const netProfit = grossProfit - periodExpenses;

    // Store detailed data for display
    setProductionDetails(productionSales);
    setSampleDetails(sampleSales);
    setExpressDetails(expressSales);
    setExpressSampleDetails(expressSamplesSales);
    setProductionCostDetails(productionCosts);
    setSampleCostDetails(sampleCosts);
    setExpressCostDetails(expressCosts);
    setExpressSampleCostDetails(expressSampleCosts);
    setCostsByCategory(costsByCategory);
    setExpensesByCategory(expensesByCategory);

    // Return data in a structured format
    return [
      { category: 'Ventas', item: 'Total Ventas', amount: totalSales },
      { category: 'Ventas', item: 'Producciones', amount: productionSales.reduce((sum, p) => sum + p.value, 0) },
      { category: 'Ventas', item: 'Muestras', amount: sampleSales.reduce((sum, s) => sum + s.value, 0) },
      { category: 'Ventas', item: 'Producciones Express', amount: expressSales.reduce((sum, e) => sum + e.value, 0) },
      { category: 'Ventas', item: 'Muestras Express', amount: expressSamplesSales.reduce((sum, es) => sum + es.value, 0) },
      { category: 'Compras', item: 'Costos de Producción', amount: -totalCosts },
      { category: 'Compras', item: 'Costos de Producciones', amount: -totalProductionCosts },
      { category: 'Compras', item: 'Costos de Muestras', amount: -totalSampleCosts },
      { category: 'Compras', item: 'Costos Express', amount: -(totalExpressCosts + totalExpressSampleCosts) },
      { category: 'Utilidad Bruta', item: 'Utilidad Bruta', amount: grossProfit },
      { category: 'Gastos', item: 'Gastos Operativos', amount: -periodExpenses },
      { category: 'Utilidad Neta', item: 'Utilidad Neta', amount: netProfit }
    ];
  };
  
  // Accounts Receivable Report
  const generateAccountsReceivable = async () => {
    // Get all client invoices with balance > 0
    const clientInvoicesWithBalance = invoices.filter(inv => 
      inv.entity_type === 'client' && 
      !inv.voided &&
      inv.balance > 0
    );
    
    // Group by client
    const clientBalances: {[key: string]: number} = {};
    
    clientInvoicesWithBalance.forEach(inv => {
      const clientId = inv.entity_id;
      if (!clientBalances[clientId]) {
        clientBalances[clientId] = 0;
      }
      clientBalances[clientId] += inv.balance;
    });
    
    // Convert to array format
    return Object.entries(clientBalances).map(([clientId, balance]) => {
      const client = clients.find(c => c.id === clientId);
      return {
        clientId,
        clientName: client?.name || 'Cliente desconocido',
        balance
      };
    }).sort((a, b) => b.balance - a.balance); // Sort by balance (highest first)
  };
  
  // Accounts Payable Report
  const generateAccountsPayable = async () => {
    // Get all supplier invoices with balance > 0
    const supplierInvoicesWithBalance = invoices.filter(inv => 
      inv.entity_type === 'supplier' && 
      !inv.voided &&
      inv.balance > 0
    );
    
    // Group by supplier
    const supplierBalances: {[key: string]: number} = {};
    
    supplierInvoicesWithBalance.forEach(inv => {
      const supplierId = inv.entity_id;
      if (!supplierBalances[supplierId]) {
        supplierBalances[supplierId] = 0;
      }
      supplierBalances[supplierId] += inv.balance;
    });
    
    // Convert to array format
    return Object.entries(supplierBalances).map(([supplierId, balance]) => {
      const supplier = suppliers.find(s => s.id === supplierId);
      return {
        supplierId,
        supplierName: supplier?.name || 'Proveedor desconocido',
        balance
      };
    }).sort((a, b) => b.balance - a.balance); // Sort by balance (highest first)
  };
  
  // Cash Flow Projection Report
  const generateCashFlowProjection = async () => {
    // Get all client invoices with balance > 0 and due date
    const clientInvoicesWithDueDate = invoices.filter(inv => 
      inv.entity_type === 'client' && 
      !inv.voided &&
      inv.balance > 0 &&
      inv.due_date
    );
    
    // Get all supplier invoices with balance > 0 and due date
    const supplierInvoicesWithDueDate = invoices.filter(inv => 
      inv.entity_type === 'supplier' && 
      !inv.voided &&
      inv.balance > 0 &&
      inv.due_date
    );
    
    // Combine and sort by due date
    const allInvoices = [
      ...clientInvoicesWithDueDate.map(inv => ({
        ...inv,
        entityName: getEntityName(inv.entity_id, inv.entity_type),
        type: 'income'
      })),
      ...supplierInvoicesWithDueDate.map(inv => ({
        ...inv,
        entityName: getEntityName(inv.entity_id, inv.entity_type),
        type: 'expense'
      }))
    ].sort((a, b) => {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });
    
    // Return formatted data
    return allInvoices.map(inv => ({
      date: inv.due_date,
      entityName: inv.entityName,
      invoiceNumber: inv.number,
      type: inv.type,
      amount: inv.balance,
      entityType: inv.entity_type
    }));
  };
  
  // VAT Sales Report
  const generateVatSales = async () => {
    // Get all client invoices of type A or B in the date range
    const vatInvoices = invoices.filter(inv => 
      inv.entity_type === 'client' && 
      !inv.voided &&
      (inv.type === 'A' || inv.type === 'B') &&
      new Date(inv.date) >= new Date(startDate) && 
      new Date(inv.date) <= new Date(endDate)
    );
    
    // Return formatted data
    return vatInvoices.map(inv => {
      const client = clients.find(c => c.id === inv.entity_id);
      return {
        date: inv.date,
        invoiceNumber: inv.number,
        clientName: client?.name || 'Cliente desconocido',
        clientCuit: client?.cuit || 'Sin CUIT',
        type: inv.type,
        subtotal: inv.subtotal,
        tax: inv.tax,
        total: inv.total
      };
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };
  
  // VAT Purchases Report
// VAT Purchases Report  ──  Facturas de proveedor  +  Gastos con IVA
const generateVatPurchases = async () => {
  /* 1. FACTURAS DE PROVEEDOR (YA EXISTENTE) */
  const vatInvoices = invoices.filter(inv =>
    inv.entity_type === 'supplier' &&
    !inv.voided &&
    ['A', 'B', 'C'].includes(inv.type) &&
    new Date(inv.date) >= new Date(startDate) &&
    new Date(inv.date) <= new Date(endDate)
  ).map(inv => {
    const supplier = suppliers.find(s => s.id === inv.entity_id);
    return {
      date: inv.date,
      invoiceNumber: inv.number,
      supplierName: supplier?.name || 'Proveedor desconocido',
      supplierCuit: supplier?.cuit || 'Sin CUIT',
      type: inv.type,                 // A, B o C
      subtotal: inv.subtotal,
      tax:      inv.tax,              // IVA crédito
      total:    inv.total
    };
  });

  /* 2. GASTOS CON IVA */
  const periodExpenses = expenses.filter(exp =>
    exp.vat > 0 &&                           // solo los que tienen alícuota
    new Date(exp.date) >= new Date(startDate) &&
    new Date(exp.date) <= new Date(endDate)
  ).map(exp => {
    const supplier = suppliers.find(s => s.id === exp.supplier_id);
    // Neto = bruto - IVA
    const net = exp.amount - (exp.vat_amount ?? exp.amount * (exp.vat / 100));
    return {
      date: exp.date,
      invoiceNumber: `GASTO-${exp.id}`,      // o '' si no querés mostrar nada
      supplierName: supplier?.name || '—',
      supplierCuit: supplier?.cuit || '—',
      type: 'G',                             // para distinguirlos
      subtotal: net,
      tax: exp.vat_amount ?? exp.amount * (exp.vat / 100),
      total: exp.amount
    };
  });

  /* 3. DEVOLVEMOS TODO JUNTO, ORDENADO POR FECHA */
  return [...vatInvoices, ...periodExpenses]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

  
  // Sales by Client Report
  const generateSalesByClient = async () => {
    // Get all client invoices in the date range
    const clientInvoices = invoices.filter(inv => 
      inv.entity_type === 'client' && 
      !inv.voided &&
      new Date(inv.date) >= new Date(startDate) && 
      new Date(inv.date) <= new Date(endDate)
    );
    
    // Group by client
    const clientSales: {[key: string]: number} = {};
    
    clientInvoices.forEach(inv => {
      const clientId = inv.entity_id;
      if (!clientSales[clientId]) {
        clientSales[clientId] = 0;
      }
      clientSales[clientId] += inv.total;
    });
    
    // Convert to array format
    return Object.entries(clientSales).map(([clientId, total]) => {
      const client = clients.find(c => c.id === clientId);
      return {
        clientId,
        clientName: client?.name || 'Cliente desconocido',
        total,
        percentage: clientInvoices.length > 0 ? 
          (total / clientInvoices.reduce((sum, inv) => sum + inv.total, 0)) * 100 : 0
      };
    }).sort((a, b) => b.total - a.total); // Sort by total (highest first)
  };
  
  // Purchases by Supplier Report
  const generatePurchasesBySupplier = async () => {
    // Get all supplier invoices in the date range
    const supplierInvoices = invoices.filter(inv => 
      inv.entity_type === 'supplier' && 
      !inv.voided &&
      new Date(inv.date) >= new Date(startDate) && 
      new Date(inv.date) <= new Date(endDate)
    );
    
    // Group by supplier
    const supplierPurchases: {[key: string]: number} = {};
    
    supplierInvoices.forEach(inv => {
      const supplierId = inv.entity_id;
      if (!supplierPurchases[supplierId]) {
        supplierPurchases[supplierId] = 0;
      }
      supplierPurchases[supplierId] += inv.total;
    });
    
    // Convert to array format
    return Object.entries(supplierPurchases).map(([supplierId, total]) => {
      const supplier = suppliers.find(s => s.id === supplierId);
      return {
        supplierId,
        supplierName: supplier?.name || 'Proveedor desconocido',
        total,
        percentage: supplierInvoices.length > 0 ? 
          (total / supplierInvoices.reduce((sum, inv) => sum + inv.total, 0)) * 100 : 0
      };
    }).sort((a, b) => b.total - a.total); // Sort by total (highest first)
  };
  
  // Bank Reconciliation Report
  const generateBankReconciliation = async () => {
    // Get all payments in the date range
    const periodPayments = payments.filter(pay => 
      !pay.voided &&
      new Date(pay.date) >= new Date(startDate) && 
      new Date(pay.date) <= new Date(endDate)
    );
    
    // Get all invoices in the date range
    const periodInvoices = invoices.filter(inv => 
      !inv.voided &&
      new Date(inv.date) >= new Date(startDate) && 
      new Date(inv.date) <= new Date(endDate)
    );
    
    // Get all expenses in the date range
    const periodExpenses = expenses.filter(exp => 
      new Date(exp.date) >= new Date(startDate) && 
      new Date(exp.date) <= new Date(endDate)
    );
    
    // Calculate cash inflows (client payments)
    const clientPayments = periodPayments.filter(pay => {
      const invoice = invoices.find(inv => inv.id === pay.invoice_id);
      return invoice?.entity_type === 'client';
    });
    
    const totalCashInflows = clientPayments.reduce((sum, pay) => sum + pay.amount, 0);
    
    // Calculate cash outflows (supplier payments + expenses)
    const supplierPayments = periodPayments.filter(pay => {
      const invoice = invoices.find(inv => inv.id === pay.invoice_id);
      return invoice?.entity_type === 'supplier';
    });
    
    const totalSupplierPayments = supplierPayments.reduce((sum, pay) => sum + pay.amount, 0);
    const totalExpensePayments = periodExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    const totalCashOutflows = totalSupplierPayments + totalExpensePayments;
    
    // Calculate net cash flow
    const netCashFlow = totalCashInflows - totalCashOutflows;
    
    // Return reconciliation data
    return [
      { date: startDate, entityName: 'Saldo Inicial', invoiceNumber: '-', type: 'balance', amount: 0 },
      ...clientPayments.map(pay => {
        const invoice = invoices.find(inv => inv.id === pay.invoice_id);
        return {
          date: pay.date,
          entityName: getEntityName(invoice?.entity_id || '', invoice?.entity_type || ''),
          invoiceNumber: invoice?.number || pay.reference || '-',
          type: 'income',
          amount: pay.amount
        };
      }),
      ...supplierPayments.map(pay => {
        const invoice = invoices.find(inv => inv.id === pay.invoice_id);
        return {
          date: pay.date,
          entityName: getEntityName(invoice?.entity_id || '', invoice?.entity_type || ''),
          invoiceNumber: invoice?.number || pay.reference || '-',
          type: 'expense',
          amount: -pay.amount
        };
      }),
      ...periodExpenses.map(exp => ({
        date: exp.date,
        entityName: exp.supplier_id ? getEntityName(exp.supplier_id, 'supplier') : 'Gasto General',
        invoiceNumber: '-',
        type: 'expense',
        amount: -exp.amount
      })),
      { date: endDate, entityName: 'Saldo Final', invoiceNumber: '-', type: 'balance', amount: netCashFlow }
    ];
  };
  
  // Financial Indicators Report
  const generateFinancialIndicators = async () => {
    // Esta función ha sido reemplazada por generateExpressMetrics
    return [];
  };
  
  // Calculate costs (supplier payments)
  const calculateCosts = (supplierPayments: any[]): { totalCosts: number, costsByCategory: Record<string, number> } => {
    const costsByCategory: Record<string, number> = {};
    
    supplierPayments.forEach(payment => {
      let supplier;
      if (payment.invoice_id) {
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        supplier = suppliers.find(s => s.id === invoice?.entity_id);
      } else if (payment.reference?.startsWith('supplier:')) {
        const supplierId = payment.reference.replace('supplier:', '');
        supplier = suppliers.find(s => s.id === supplierId);
      }
      
      const category = supplier?.category || 'Sin categoría';
      
      if (!costsByCategory[category]) {
        costsByCategory[category] = 0;
      }
      
      costsByCategory[category] += payment.amount;
    });
    
    const totalCosts = supplierPayments.reduce((sum, payment) => sum + payment.amount, 0);
    
    return { totalCosts, costsByCategory };
  };
  
  // Calculate expenses
  const calculateExpenses = (expenses: any[]): { totalExpenses: number, expensesByCategory: Record<string, number> } => {
    const expensesByCategory: Record<string, number> = {};
    
    expenses.forEach(expense => {
      const category = expenseCategories.find(c => c.id === expense.category_id)?.name || 'Sin categoría';
      
      if (!expensesByCategory[category]) {
        expensesByCategory[category] = 0;
      }
      
      // Include VAT in the expense amount
      expensesByCategory[category] += expense.amount;
    });
    
    const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    return { totalExpenses, expensesByCategory };
  };

  const [salesBySource, setSalesBySource] = useState<Record<string, number>>({});

  // Helper function to get entity name
  const getEntityName = (entityId: string, entityType: string): string => {
    if (entityType === 'client') {
      const client = clients.find(c => c.id === entityId);
      return client?.name || 'Cliente desconocido';
    } else {
      const supplier = suppliers.find(s => s.id === entityId);
      return supplier?.name || 'Proveedor desconocido';
    }
  };
  
  // Export report to PDF
  const exportToPDF = () => {
    if (!reportData[selectedReport] || reportData[selectedReport].length === 0) {
      setError('No hay datos disponibles para exportar. Genera un reporte primero.');
      setShowErrorModal(true);
      return;
    }
    
    const doc = new jsPDF();
    
    // Set title
    doc.setFontSize(18);
    doc.text(getReportTitle(selectedReport), 14, 20);
    
    // Set date range
    doc.setFontSize(12);
    doc.text(`Período: ${formatArgentinaDate(startDate)} - ${formatArgentinaDate(endDate)}`, 14, 30);
    
    // Get table data based on selected report
    let tableData: any[] = [];
    let tableColumns: string[] = [];
    
    switch (selectedReport) {
      case 'income-statement':
        tableColumns = ['Categoría', 'Concepto', 'Monto', '%'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.category,
          item.item,
          formatCurrency(item.amount),
          item.amount !== 0 ? `${((Math.abs(item.amount) / (reportData[selectedReport].find((r: any) => r.category === 'Ingresos de Efectivo')?.amount || 1)) * 100).toFixed(2)}%` : '0%'
        ]);
        break;
        
      case 'income-statement-results':
        tableColumns = ['Categoría', 'Concepto', 'Monto', '%'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.category,
          item.item,
          formatCurrency(item.amount),
          item.amount !== 0 ? `${((Math.abs(item.amount) / (reportData[selectedReport].find((r: any) => r.item === 'Total Ventas')?.amount || 1)) * 100).toFixed(2)}%` : '0%'
        ]);
        break;
        
      case 'accounts-receivable':
        tableColumns = ['Cliente', 'Saldo'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.clientName,
          formatCurrency(item.balance)
        ]);
        break;
        
      case 'accounts-payable':
        tableColumns = ['Proveedor', 'Saldo'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.supplierName,
          formatCurrency(item.balance)
        ]);
        break;
        
      case 'cash-flow-projection':
        tableColumns = ['Fecha', 'Entidad', 'Factura', 'Tipo', 'Monto'];
        tableData = reportData[selectedReport].map((item: any) => [
          formatArgentinaDate(item.date),
          item.entityName,
          item.invoiceNumber,
          item.type === 'income' ? 'Ingreso' : 'Egreso',
          formatCurrency(item.amount)
        ]);
        break;
        
      case 'vat-sales':
        tableColumns = ['Fecha', 'Factura', 'Cliente', 'CUIT', 'Tipo', 'Neto', 'IVA', 'Total'];
        tableData = reportData[selectedReport].map((item: any) => [
          formatArgentinaDate(item.date),
          item.invoiceNumber,
          item.clientName,
          item.clientCuit,
          item.type,
          formatCurrency(item.subtotal),
          formatCurrency(item.tax),
          formatCurrency(item.total)
        ]);
        break;
        
      case 'vat-purchases':
        tableColumns = ['Fecha', 'Factura', 'Proveedor', 'CUIT', 'Tipo', 'Neto', 'IVA', 'Total'];
        tableData = reportData[selectedReport].map((item: any) => [
          formatArgentinaDate(item.date),
          item.invoiceNumber,
          item.supplierName,
          item.supplierCuit,
          item.type,
          formatCurrency(item.subtotal),
          formatCurrency(item.tax),
          formatCurrency(item.total)
        ]);
        break;
        
      case 'sales-by-client':
        tableColumns = ['Cliente', 'Total', 'Porcentaje'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.clientName,
          formatCurrency(item.total),
          `${item.percentage.toFixed(2)}%`
        ]);
        break;
        
      case 'purchases-by-supplier':
        tableColumns = ['Proveedor', 'Total', 'Porcentaje'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.supplierName,
          formatCurrency(item.total),
          `${item.percentage.toFixed(2)}%`
        ]);
        break;
        
      case 'express-metrics':
        tableColumns = ['Indicador', 'Valor', 'Descripción'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.indicator,
          item.value.toFixed(2),
          item.description
        ]);
        break;
        
      default:
        tableColumns = [];
        tableData = [];
    }
    
    doc.autoTable({
      head: [tableColumns],
      body: tableData,
      startY: 40,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [66, 66, 66],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      }
    });
    
    // Save the PDF
    doc.save(`reporte-${selectedReport}-${startDate}-${endDate}.pdf`);
  };
  
  // Export report to CSV
  const exportToCSV = () => {
    if (!reportData[selectedReport] || reportData[selectedReport].length === 0) {
      setError('No hay datos disponibles para exportar. Genera un reporte primero.');
      setShowErrorModal(true);
      return;
    }

    let tableColumns: string[] = [];
    let tableData: any[][] = [];

    switch (selectedReport) {
      case 'income-statement':
        tableColumns = ['Categoría', 'Ítem', 'Monto', '%'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.category,
          item.item,
          formatCurrencyForCSV(item.amount),
          `${((Math.abs(item.amount) / totalCashInflows) * 100).toFixed(2)}%`
        ]);
        break;
        
      case 'income-statement-results':
        // Para el estado de resultados, exportar la información detallada
        tableColumns = ['Categoría', 'Concepto', 'Monto', '%'];
        
        // Agregar ventas totales
        const totalSales = reportData[selectedReport].find((r: any) => r.item === 'Total Ventas')?.amount || 0;
        tableData.push(['Ventas', 'Total Ventas', formatCurrencyForCSV(totalSales), '100%']);
        
        // Agregar producciones con detalles
        if (productionDetails.length > 0) {
          tableData.push(['', 'Producciones', formatCurrencyForCSV(productionDetails.reduce((sum, p) => sum + p.value, 0)), `${((productionDetails.reduce((sum, p) => sum + p.value, 0) / totalSales) * 100).toFixed(2)}%`]);
          productionDetails.forEach(prod => {
            tableData.push(['', `  ${prod.name} (${prod.progress}% avance)`, formatCurrencyForCSV(prod.value), `${((prod.value / totalSales) * 100).toFixed(2)}%`]);
          });
        }
        
        // Agregar muestras con detalles
        if (sampleDetails.length > 0) {
          tableData.push(['', 'Muestras', formatCurrencyForCSV(sampleDetails.reduce((sum, s) => sum + s.value, 0)), `${((sampleDetails.reduce((sum, s) => sum + s.value, 0) / totalSales) * 100).toFixed(2)}%`]);
          sampleDetails.forEach(sample => {
            tableData.push(['', `  ${sample.name} (${sample.progress}% avance)`, formatCurrencyForCSV(sample.value), `${((sample.value / totalSales) * 100).toFixed(2)}%`]);
          });
        }
        
        // Agregar producciones express con detalles
        if (expressDetails.length > 0) {
          tableData.push(['', 'Producciones Express', formatCurrencyForCSV(expressDetails.reduce((sum, e) => sum + e.value, 0)), `${((expressDetails.reduce((sum, e) => sum + e.value, 0) / totalSales) * 100).toFixed(2)}%`]);
          expressDetails.forEach(exp => {
            tableData.push(['', `  ${exp.name} (${exp.hoodies} buzos, ${exp.tshirts} remeras)`, formatCurrencyForCSV(exp.value), `${((exp.value / totalSales) * 100).toFixed(2)}%`]);
          });
        }
        
        // Agregar muestras express con detalles
        if (expressSampleDetails.length > 0) {
          tableData.push(['', 'Muestras Express', formatCurrencyForCSV(expressSampleDetails.reduce((sum, es) => sum + es.value, 0)), `${((expressSampleDetails.reduce((sum, es) => sum + es.value, 0) / totalSales) * 100).toFixed(2)}%`]);
          expressSampleDetails.forEach(es => {
            tableData.push(['', `  ${es.name} (${es.hoodies} buzos, ${es.tshirts} remeras)`, formatCurrencyForCSV(es.value), `${((es.value / totalSales) * 100).toFixed(2)}%`]);
          });
        }
        
        // Agregar costos totales
        const totalCosts = reportData[selectedReport].find((r: any) => r.item === 'Costos de Producción')?.amount || 0;
        tableData.push(['Compras', 'Costos de Producción', formatCurrencyForCSV(totalCosts), `${((Math.abs(totalCosts) / totalSales) * 100).toFixed(2)}%`]);
        
        // Agregar costos de producciones con detalles
        if (productionCostDetails.length > 0) {
          tableData.push(['', 'Costos de Producciones', formatCurrencyForCSV(-productionCostDetails.reduce((sum, c) => sum + c.cost, 0)), `${((productionCostDetails.reduce((sum, c) => sum + c.cost, 0) / totalSales) * 100).toFixed(2)}%`]);
          productionCostDetails.forEach(cost => {
            tableData.push(['', `  ${cost.productionName} - ${cost.supplierName} (${cost.supplierCategory})`, formatCurrencyForCSV(-cost.cost), `${((cost.cost / totalSales) * 100).toFixed(2)}%`]);
          });
        }
        
        // Agregar costos de muestras con detalles
        if (sampleCostDetails.length > 0) {
          tableData.push(['', 'Costos de Muestras', formatCurrencyForCSV(-sampleCostDetails.reduce((sum, c) => sum + c.cost, 0)), `${((sampleCostDetails.reduce((sum, c) => sum + c.cost, 0) / totalSales) * 100).toFixed(2)}%`]);
          sampleCostDetails.forEach(cost => {
            tableData.push(['', `  ${cost.sampleName} - ${cost.supplierName} (${cost.supplierCategory})`, formatCurrencyForCSV(-cost.cost), `${((cost.cost / totalSales) * 100).toFixed(2)}%`]);
          });
        }
        
        // Agregar costos express con detalles
        if (expressCostDetails.length > 0 || expressSampleCostDetails.length > 0) {
          const totalExpressCosts = expressCostDetails.reduce((sum, c) => sum + c.totalCost, 0) + expressSampleCostDetails.reduce((sum, c) => sum + c.totalCost, 0);
          tableData.push(['', 'Costos Express', formatCurrencyForCSV(-totalExpressCosts), `${((totalExpressCosts / totalSales) * 100).toFixed(2)}%`]);
          
          expressCostDetails.forEach(cost => {
            tableData.push(['', `  ${cost.name} - Buzos: ${cost.hoodies} × $${expressCostConfig.hoodieCost}, Remeras: ${cost.tshirts} × $${expressCostConfig.tshirtCost}`, formatCurrencyForCSV(-cost.totalCost), `${((cost.totalCost / totalSales) * 100).toFixed(2)}%`]);
          });
          
          expressSampleCostDetails.forEach(cost => {
            tableData.push(['', `  ${cost.name} - Buzos: ${cost.hoodies} × $${expressCostConfig.hoodieCost}, Remeras: ${cost.tshirts} × $${expressCostConfig.tshirtCost}`, formatCurrencyForCSV(-cost.totalCost), `${((cost.totalCost / totalSales) * 100).toFixed(2)}%`]);
          });
        }
        
        // Agregar utilidad bruta
        const grossProfit = reportData[selectedReport].find((r: any) => r.item === 'Utilidad Bruta')?.amount || 0;
        tableData.push(['Utilidad Bruta', 'Utilidad Bruta', formatCurrencyForCSV(grossProfit), `${((grossProfit / totalSales) * 100).toFixed(2)}%`]);
        
        // Agregar gastos
        const expenses = reportData[selectedReport].find((r: any) => r.item === 'Gastos Operativos')?.amount || 0;
        tableData.push(['Gastos', 'Gastos Operativos', formatCurrencyForCSV(expenses), `${((Math.abs(expenses) / totalSales) * 100).toFixed(2)}%`]);
        
        // Agregar desglose de gastos por categoría
        if (Object.keys(expensesByCategory).length > 0) {
          tableData.push(['', 'Desglose por Categoría', formatCurrencyForCSV(-Object.values(expensesByCategory).reduce((sum, amt) => sum + amt, 0)), `${((Object.values(expensesByCategory).reduce((sum, amt) => sum + amt, 0) / totalSales) * 100).toFixed(2)}%`]);
          Object.entries(expensesByCategory).forEach(([cat, amt]) => {
            if (amt !== 0) {
              tableData.push(['', `  ${cat}`, formatCurrencyForCSV(-amt), `${((amt / totalSales) * 100).toFixed(2)}%`]);
            }
          });
        }
        
        // Agregar utilidad neta
        const netProfit = reportData[selectedReport].find((r: any) => r.item === 'Utilidad Neta')?.amount || 0;
        tableData.push(['Utilidad Neta', 'Utilidad Neta', formatCurrencyForCSV(netProfit), `${((netProfit / totalSales) * 100).toFixed(2)}%`]);
        break;
        
      case 'accounts-receivable':
        tableColumns = ['Cliente', 'Saldo'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.clientName,
          formatCurrencyForCSV(item.balance)
        ]);
        break;
        
      case 'accounts-payable':
        tableColumns = ['Proveedor', 'Saldo'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.supplierName,
          formatCurrencyForCSV(item.balance)
        ]);
        break;
        
      case 'cash-flow-projection':
        tableColumns = ['Fecha', 'Entidad', 'Factura', 'Tipo', 'Monto'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.dueDate,
          item.entityName,
          item.invoiceNumber,
          item.type === 'income' ? 'Ingreso' : 'Egreso',
          formatCurrencyForCSV(item.amount)
        ]);
        break;
        
      case 'vat-sales':
        tableColumns = ['Fecha', 'Factura', 'Cliente', 'CUIT', 'Tipo', 'Neto', 'IVA', 'Total'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.date,
          item.invoiceNumber,
          item.clientName,
          item.cuit,
          item.type,
          formatCurrencyForCSV(item.subtotal),
          formatCurrencyForCSV(item.tax),
          formatCurrencyForCSV(item.total)
        ]);
        break;
        
      case 'vat-purchases':
        tableColumns = ['Fecha', 'Factura', 'Proveedor', 'CUIT', 'Tipo', 'Neto', 'IVA', 'Total'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.date,
          item.invoiceNumber,
          item.supplierName,
          item.cuit,
          item.type,
          formatCurrencyForCSV(item.subtotal),
          formatCurrencyForCSV(item.tax),
          formatCurrencyForCSV(item.total)
        ]);
        break;
        
      case 'sales-by-client':
        tableColumns = ['Cliente', 'Total', 'Porcentaje'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.clientName,
          formatCurrencyForCSV(item.total),
          `${item.percentage.toFixed(2)}%`
        ]);
        break;
        
      case 'purchases-by-supplier':
        tableColumns = ['Proveedor', 'Total', 'Porcentaje'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.supplierName,
          formatCurrencyForCSV(item.total),
          `${item.percentage.toFixed(2)}%`
        ]);
        break;
        
      case 'express-metrics':
        tableColumns = ['Métrica', 'Valor', 'Descripción'];
        tableData = reportData[selectedReport].map((item: any) => [
          item.indicator,
          typeof item.value === 'number' ? (item.value >= 1000 ? formatCurrencyForCSV(item.value) : item.value.toString()) : item.value,
          item.description
        ]);
        break;
        
      default:
        tableColumns = [];
        tableData = [];
    }
    
    // Convert data to CSV format
    const csvData = tableData.map(row => 
      row.map(cell => typeof cell === 'string' ? `"${cell}"` : cell).join(',')
    ).join('\n');
    
    // Create CSV header
    const csvHeader = tableColumns.join(',');
    
    // Combine header and data
    const csv = `${csvHeader}\n${csvData}`;
    
    // Create a blob and download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte-${selectedReport}-${startDate}-${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Get table columns based on selected report
  const getTableColumns = () => {
    switch (selectedReport) {
      case 'income-statement':
        return ['Categoría', 'Ítem', 'Monto', '%'];
      case 'accounts-receivable':
        return ['Cliente', 'Saldo'];
      case 'accounts-payable':
        return ['Proveedor', 'Saldo'];
      case 'cash-flow-projection':
        return ['Fecha', 'Entidad', 'Factura', 'Tipo', 'Monto'];
      case 'vat-sales':
        return ['Fecha', 'Factura', 'Cliente', 'CUIT', 'Tipo', 'Neto', 'IVA', 'Total'];
      case 'vat-purchases':
        return ['Fecha', 'Factura', 'Proveedor', 'CUIT', 'Tipo', 'Neto', 'IVA', 'Total'];
      case 'sales-by-client':
        return ['Cliente', 'Total', 'Porcentaje'];
      case 'purchases-by-supplier':
        return ['Proveedor', 'Total', 'Porcentaje'];
      case 'bank-reconciliation':
        return ['Fecha', 'Ingresos', 'Egresos', 'Saldo'];
      case 'express-metrics':
        return ['Métrica', 'Valor', 'Descripción'];
      default:
        return [];
    }
  };
  
  // Get report title based on selected report
  const getReportTitle = (report: ReportType): string => {
    switch (report) {
      case 'income-statement':
        return 'Estado de Flujo de Efectivo';
      case 'income-statement-results':
        return 'Estado de Resultados';
      case 'accounts-receivable':
        return 'Cuentas por Cobrar';
      case 'accounts-payable':
        return 'Cuentas por Pagar';
      case 'cash-flow-projection':
        return 'Proyección de Flujo de Caja';
      case 'vat-sales':
        return 'IVA Ventas';
      case 'vat-purchases':
        return 'IVA Compras';
      case 'sales-by-client':
        return 'Ventas por Cliente';
      case 'purchases-by-supplier':
        return 'Compras por Proveedor';
      case 'express-metrics':
        return 'Métricas Express';
      default:
        return 'Reporte';
    }
  };
  
  // Calculate totals for the current report
  const calculateReportTotals = () => {
    if (!reportData[selectedReport] || reportData[selectedReport].length === 0) {
      return null;
    }
    
    switch (selectedReport) {
      case 'income-statement':
        return {
          netCashFlow: reportData[selectedReport].find((item: any) => item.item === 'Flujo de Efectivo Neto')?.amount || 0
        };
      case 'income-statement-results':
        return {
          netProfit: reportData[selectedReport].find((item: any) => item.item === 'Utilidad Neta')?.amount || 0
        };
      case 'accounts-receivable':
        return {
          total: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.balance, 0)
        };
      case 'accounts-payable':
        return {
          total: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.balance, 0)
        };
      case 'cash-flow-projection':
        const inflows = reportData[selectedReport]
          .filter((item: any) => item.type === 'income')
          .reduce((sum: number, item: any) => sum + item.amount, 0);
        const outflows = reportData[selectedReport]
          .filter((item: any) => item.type === 'expense')
          .reduce((sum: number, item: any) => sum + item.amount, 0);
        return {
          inflows,
          outflows,
          netFlow: inflows - outflows
        };
      case 'vat-sales':
        return {
          subtotal: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.subtotal, 0),
          tax: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.tax, 0),
          total: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.total, 0)
        };
      case 'vat-purchases':
        return {
          subtotal: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.subtotal, 0),
          tax: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.tax, 0),
          total: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.total, 0)
        };
      case 'sales-by-client':
        return {
          total: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.total, 0)
        };
      case 'purchases-by-supplier':
        return {
          total: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.total, 0)
        };
      case 'bank-reconciliation':
        return {
          inflows: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.inflow, 0),
          outflows: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.outflow, 0),
          balance: reportData[selectedReport].reduce((sum: number, item: any) => sum + item.balance, 0)
        };
      case 'express-metrics':
        return {
          totalOrders: reportData[selectedReport].find((item: any) => item.indicator === 'Total Pedidos Express')?.value || 0,
          totalSamples: reportData[selectedReport].find((item: any) => item.indicator === 'Total Muestras Express')?.value || 0,
          totalRevenue: reportData[selectedReport].find((item: any) => item.indicator === 'Ingresos Express')?.value || 0,
          avgTicket: reportData[selectedReport].find((item: any) => item.indicator === 'Ticket Promedio')?.value || 0
        };
      default:
        return null;
    }
  };
  
  // Render report table based on selected report
  const renderReportTable = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin mr-2" />
          <p className="text-gray-600 dark:text-gray-400">Generando reporte...</p>
        </div>
      );
    }
    
    if (!reportData[selectedReport] || reportData[selectedReport].length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="h-12 w-12 text-amber-500 dark:text-amber-400 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No hay datos disponibles para el período seleccionado</p>
        </div>
      );
    }
    
    switch (selectedReport) {
case 'income-statement': {
  // ─────────── cálculo previo ───────────
  const rows = reportData['income-statement'];
  const totalCashInflows = rows.find((r: any) => r.category === 'Ingresos de Efectivo')?.amount || 1;

  // utilitario para % (valor absoluto sobre ingresos de efectivo)
  const pct = (amount: number) =>
    `${((Math.abs(amount) / totalCashInflows) * 100).toFixed(2)} %`;

  return (
    <table className="min-w-full table-fixed">
      <thead className="bg-gray-50 dark:bg-gray-800">
        <tr>
          <th className="w-1/4 px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:text-gray-400 uppercase">
            Categoría
          </th>
          <th className="w-1/3 px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:text-gray-400 uppercase">
            Concepto
          </th>
          <th className="w-1/4 px-4 py-3 text-right text-xs font-medium tracking-wider text-gray-500 dark:text-gray-400 uppercase">
            Monto
          </th>
          <th className="w-1/6 px-4 py-3 text-right text-xs font-medium tracking-wider text-gray-500 dark:text-gray-400 uppercase">
            %
          </th>
        </tr>
      </thead>

      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">

{/* ——— Ingresos de Efectivo + desglose ——— */}
<tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
  <td className="px-4 py-3">Ingresos de Efectivo</td>
  <td className="px-4 py-3">Total Ingresos</td>
  <td className="px-4 py-3 text-right">
    {formatCurrency(totalCashInflows)}
  </td>
  <td className="px-4 py-3 text-right">100 %</td>
</tr>

{/* ➊ —---------- DESGLOSE ----------- */}
{Object.entries(salesBySource).map(([label, amt]) =>
  amt !== 0 && (
    <tr key={`sale-${label}`}>
      {/* celdas indentadas */}
      <td className="px-4 py-2 pl-8 text-sm text-gray-700 dark:text-gray-300"></td>
      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
        {label}
      </td>
      <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
        {formatCurrency(amt)}
      </td>
      <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
        {pct(amt)}
      </td>
    </tr>
))
}
{/* ➊ —---------- FIN DESGLOSE ----------- */}


        {/* ——— Egresos de Efectivo ——— */}
        <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
          <td className="px-4 py-3">Egresos de Efectivo</td>
          <td className="px-4 py-3">Pagos a Proveedores</td>
          <td className="px-4 py-3 text-right">
            {formatCurrency(rows.find((r: any) => r.category === 'Egresos de Efectivo')?.amount || 0)}
          </td>
          <td className="px-4 py-3 text-right">
            {pct(rows.find((r: any) => r.category === 'Egresos de Efectivo')?.amount || 0)}
          </td>
        </tr>

        {/* desglose de pagos por categoría de proveedor */}
        {Object.entries(costsByCategory).map(([cat, amt]) => (
          amt !== 0 && (
            <tr key={`cost-${cat}`}>
              <td className="px-4 py-2 pl-8 text-sm text-gray-700 dark:text-gray-300"> </td>
              <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{cat}</td>
              <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                {formatCurrency(-amt)}
              </td>
              <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                {pct(-amt)}
              </td>
            </tr>
          )
        ))}

        {/* ——— Flujo Operativo ——— */}
        <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
          <td className="px-4 py-3" colSpan={2}>Flujo Operativo</td>
          <td className="px-4 py-3 text-right">
            {formatCurrency(rows.find((r: any) => r.category === 'Flujo Operativo')?.amount || 0)}
          </td>
          <td className="px-4 py-3 text-right">
            {pct(rows.find((r: any) => r.category === 'Flujo Operativo')?.amount || 0)}
          </td>
        </tr>

        {/* ——— Gastos ——— */}
        <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
          <td className="px-4 py-3">Gastos</td>
          <td className="px-4 py-3">Gastos operativos</td>
          <td className="px-4 py-3 text-right">
            {formatCurrency(rows.find((r: any) => r.category === 'Gastos')?.amount || 0)}
          </td>
          <td className="px-4 py-3 text-right">
            {pct(rows.find((r: any) => r.category === 'Gastos')?.amount || 0)}
          </td>
        </tr>

        {/* desglose de gastos por categoría */}
        {Object.entries(expensesByCategory).map(([cat, amt]) => (
          amt !== 0 && (
            <tr key={`exp-${cat}`}>
              <td className="px-4 py-2 pl-8 text-sm text-gray-700 dark:text-gray-300"> </td>
              <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{cat}</td>
              <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                {formatCurrency(-amt)}
              </td>
              <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                {pct(-amt)}
              </td>
            </tr>
          )
        ))}

        {/* ——— Flujo de Efectivo Neto ——— */}
        <tr className="bg-gray-50 dark:bg-gray-800 font-bold">
          <td className="px-4 py-3" colSpan={2}>Flujo de Efectivo Neto</td>
          <td className={`px-4 py-3 text-right ${
            rows.find((r: any) => r.category === 'Flujo de Efectivo Neto')?.amount >= 0
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            {formatCurrency(rows.find((r: any) => r.category === 'Flujo de Efectivo Neto')?.amount || 0)}
          </td>
          <td className="px-4 py-3 text-right">
            {pct(rows.find((r: any) => r.category === 'Flujo de Efectivo Neto')?.amount || 0)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
      
      case 'income-statement-results': {
        const rows = reportData['income-statement-results'];
        const totalSales = rows.find((r: any) => r.item === 'Total Ventas')?.amount || 1;

        const pct = (amount: number) =>
          `${((Math.abs(amount) / totalSales) * 100).toFixed(2)} %`;

        const toggleSection = (section: keyof typeof expandedSections) => {
          setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
          }));
        };

        return (
          <div>
            <table className="min-w-full table-fixed">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="w-1/4 px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:text-gray-400 uppercase">
                    Categoría
                  </th>
                  <th className="w-1/3 px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 dark:text-gray-400 uppercase">
                    Concepto
                  </th>
                  <th className="w-1/4 px-4 py-3 text-right text-xs font-medium tracking-wider text-gray-500 dark:text-gray-400 uppercase">
                    Monto
                  </th>
                  <th className="w-1/6 px-4 py-3 text-right text-xs font-medium tracking-wider text-gray-500 dark:text-gray-400 uppercase">
                    %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {/* Ventas */}
                <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                  <td className="px-4 py-3">Ventas</td>
                  <td className="px-4 py-3">Total Ventas</td>
                  <td className="px-4 py-3 text-right">
                    {formatCurrency(rows.find((r: any) => r.item === 'Total Ventas')?.amount || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">100 %</td>
                </tr>

                {/* Producciones - Collapsible */}
                {productionDetails.length > 0 && (
                  <>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleSection('productions')}>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 flex items-center">
                        <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${expandedSections.productions ? 'rotate-90' : ''}`} />
                        Producciones ({productionDetails.length})
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(productionDetails.reduce((sum, p) => sum + p.value, 0))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(productionDetails.reduce((sum, p) => sum + p.value, 0))}
                      </td>
                    </tr>
                    {expandedSections.productions && productionDetails.map((prod, index) => (
                      <tr key={`prod-${index}`}>
                        <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {prod.name} ({prod.progress}% avance)
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {formatCurrency(prod.value)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {pct(prod.value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Muestras - Collapsible */}
                {sampleDetails.length > 0 && (
                  <>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleSection('samples')}>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 flex items-center">
                        <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${expandedSections.samples ? 'rotate-90' : ''}`} />
                        Muestras ({sampleDetails.length})
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(sampleDetails.reduce((sum, s) => sum + s.value, 0))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(sampleDetails.reduce((sum, s) => sum + s.value, 0))}
                      </td>
                    </tr>
                    {expandedSections.samples && sampleDetails.map((sample, index) => (
                      <tr key={`sample-${index}`}>
                        <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {sample.name} ({sample.progress}% avance)
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {formatCurrency(sample.value)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {pct(sample.value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Producciones Express - Collapsible */}
                {expressDetails.length > 0 && (
                  <>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleSection('express')}>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 flex items-center">
                        <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${expandedSections.express ? 'rotate-90' : ''}`} />
                        Producciones Express ({expressDetails.length})
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(expressDetails.reduce((sum, e) => sum + e.value, 0))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(expressDetails.reduce((sum, e) => sum + e.value, 0))}
                      </td>
                    </tr>
                    {expandedSections.express && expressDetails.map((exp, index) => (
                      <tr key={`exp-${index}`}>
                        <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {exp.name} ({exp.hoodies} buzos, {exp.tshirts} remeras)
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {formatCurrency(exp.value)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {pct(exp.value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Muestras Express - Collapsible */}
                {expressSampleDetails.length > 0 && (
                  <>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleSection('expressSamples')}>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 flex items-center">
                        <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${expandedSections.expressSamples ? 'rotate-90' : ''}`} />
                        Muestras Express ({expressSampleDetails.length})
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(expressSampleDetails.reduce((sum, es) => sum + es.value, 0))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(expressSampleDetails.reduce((sum, es) => sum + es.value, 0))}
                      </td>
                    </tr>
                    {expandedSections.expressSamples && expressSampleDetails.map((es, index) => (
                      <tr key={`es-${index}`}>
                        <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {es.name} ({es.hoodies} buzos, {es.tshirts} remeras)
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {formatCurrency(es.value)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {pct(es.value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Compras */}
                <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                  <td className="px-4 py-3">Compras</td>
                  <td className="px-4 py-3">Costos de Producción</td>
                  <td className="px-4 py-3 text-right">
                    {formatCurrency(rows.find((r: any) => r.item === 'Costos de Producción')?.amount || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {pct(rows.find((r: any) => r.item === 'Costos de Producción')?.amount || 0)}
                  </td>
                </tr>

                {/* Costos de Producciones - Collapsible */}
                {productionCostDetails.length > 0 && (
                  <>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleSection('productionCosts')}>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 flex items-center">
                        <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${expandedSections.productionCosts ? 'rotate-90' : ''}`} />
                        Costos de Producciones ({productionCostDetails.length})
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(-productionCostDetails.reduce((sum, c) => sum + c.cost, 0))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(-productionCostDetails.reduce((sum, c) => sum + c.cost, 0))}
                      </td>
                    </tr>
                    {expandedSections.productionCosts && productionCostDetails.map((cost, index) => (
                      <tr key={`prod-cost-${index}`}>
                        <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {cost.productionName} - {cost.supplierName} ({cost.supplierCategory})
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {formatCurrency(-cost.cost)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {pct(-cost.cost)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Costos de Muestras - Collapsible */}
                {sampleCostDetails.length > 0 && (
                  <>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleSection('sampleCosts')}>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 flex items-center">
                        <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${expandedSections.sampleCosts ? 'rotate-90' : ''}`} />
                        Costos de Muestras ({sampleCostDetails.length})
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(-sampleCostDetails.reduce((sum, c) => sum + c.cost, 0))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(-sampleCostDetails.reduce((sum, c) => sum + c.cost, 0))}
                      </td>
                    </tr>
                    {expandedSections.sampleCosts && sampleCostDetails.map((cost, index) => (
                      <tr key={`sample-cost-${index}`}>
                        <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {cost.sampleName} - {cost.supplierName} ({cost.supplierCategory})
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {formatCurrency(-cost.cost)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                          {pct(-cost.cost)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Costos Express - Collapsible */}
                {(expressCostDetails.length > 0 || expressSampleCostDetails.length > 0) && (
                  <>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleSection('expressCosts')}>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 flex items-center">
                        <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${expandedSections.expressCosts ? 'rotate-90' : ''}`} />
                        Costos Express ({expressCostDetails.length + expressSampleCostDetails.length})
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(-(expressCostDetails.reduce((sum, c) => sum + c.totalCost, 0) + 
                                         expressSampleCostDetails.reduce((sum, c) => sum + c.totalCost, 0)))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(-(expressCostDetails.reduce((sum, c) => sum + c.totalCost, 0) + 
                              expressSampleCostDetails.reduce((sum, c) => sum + c.totalCost, 0)))}
                      </td>
                    </tr>
                    {expandedSections.expressCosts && (
                      <>
                        {expressCostDetails.map((cost, index) => (
                          <tr key={`exp-cost-${index}`}>
                            <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                            <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                              {cost.name} - Buzos: {cost.hoodies} × ${expressCostConfig.hoodieCost}, 
                              Remeras: {cost.tshirts} × ${expressCostConfig.tshirtCost}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                              {formatCurrency(-cost.totalCost)}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                              {pct(-cost.totalCost)}
                            </td>
                          </tr>
                        ))}
                        {expressSampleCostDetails.map((cost, index) => (
                          <tr key={`es-cost-${index}`}>
                            <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                            <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                              {cost.name} - Buzos: {cost.hoodies} × ${expressCostConfig.hoodieCost}, 
                              Remeras: {cost.tshirts} × ${expressCostConfig.tshirtCost}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                              {formatCurrency(-cost.totalCost)}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                              {pct(-cost.totalCost)}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </>
                )}

                {/* Utilidad Bruta */}
                <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                  <td className="px-4 py-3" colSpan={2}>Utilidad Bruta</td>
                  <td className="px-4 py-3 text-right">
                    {formatCurrency(rows.find((r: any) => r.item === 'Utilidad Bruta')?.amount || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {pct(rows.find((r: any) => r.item === 'Utilidad Bruta')?.amount || 0)}
                  </td>
                </tr>

                {/* Gastos */}
                <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
                  <td className="px-4 py-3">Gastos</td>
                  <td className="px-4 py-3">Gastos Operativos</td>
                  <td className="px-4 py-3 text-right">
                    {formatCurrency(rows.find((r: any) => r.item === 'Gastos Operativos')?.amount || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {pct(rows.find((r: any) => r.item === 'Gastos Operativos')?.amount || 0)}
                  </td>
                </tr>

                {/* Desglose de gastos por categoría - Collapsible */}
                {Object.keys(expensesByCategory).length > 0 && (
                  <>
                    <tr className="bg-gray-50 dark:bg-gray-800 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toggleSection('expenses')}>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 flex items-center">
                        <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${expandedSections.expenses ? 'rotate-90' : ''}`} />
                        Desglose por Categoría ({Object.keys(expensesByCategory).length})
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(-Object.values(expensesByCategory).reduce((sum, amt) => sum + amt, 0))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(-Object.values(expensesByCategory).reduce((sum, amt) => sum + amt, 0))}
                      </td>
                    </tr>
                    {expandedSections.expenses && Object.entries(expensesByCategory).map(([cat, amt]) => (
                      amt !== 0 && (
                        <tr key={`exp-${cat}`}>
                          <td className="px-4 py-2 pl-12 text-sm text-gray-700 dark:text-gray-300"></td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{cat}</td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                            {formatCurrency(-amt)}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                            {pct(-amt)}
                          </td>
                        </tr>
                      )
                    ))}
                  </>
                )}

                {/* Utilidad Neta */}
                <tr className="bg-gray-50 dark:bg-gray-800 font-bold">
                  <td className="px-4 py-3" colSpan={2}>Utilidad Neta</td>
                  <td className={`px-4 py-3 text-right ${
                    rows.find((r: any) => r.item === 'Utilidad Neta')?.amount >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(rows.find((r: any) => r.item === 'Utilidad Neta')?.amount || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {pct(rows.find((r: any) => r.item === 'Utilidad Neta')?.amount || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
      
      case 'accounts-receivable':
        return (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Saldo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData[selectedReport].map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.clientName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(item.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'accounts-payable':
        return (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Proveedor
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Saldo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData[selectedReport].map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.supplierName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-red-600 dark:text-red-400">
                    {formatCurrency(item.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'cash-flow-projection':
        return (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Entidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Factura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Monto
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData[selectedReport].map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatArgentinaDate(item.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.entityName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.type === 'income'
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}
                    >
                      {item.type === 'income' ? 'Ingreso' : 'Egreso'}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-right ${
                    item.type === 'income'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(item.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'vat-sales':
        return (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Factura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  CUIT
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Neto
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  IVA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData[selectedReport].map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatArgentinaDate(item.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.clientName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.clientCuit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {item.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {formatCurrency(item.subtotal)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {formatCurrency(item.tax)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'vat-purchases':
        return (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Factura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Proveedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  CUIT
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Neto
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  IVA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData[selectedReport].map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatArgentinaDate(item.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.supplierName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.supplierCuit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {item.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {formatCurrency(item.subtotal)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {formatCurrency(item.tax)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-red-600 dark:text-red-400">
                    {formatCurrency(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'sales-by-client':
        return (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Porcentaje
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData[selectedReport].map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.clientName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(item.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {item.percentage.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'purchases-by-supplier':
        return (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Proveedor
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Porcentaje
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData[selectedReport].map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.supplierName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-red-600 dark:text-red-400">
                    {formatCurrency(item.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {item.percentage.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'bank-reconciliation':
        return (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ingresos
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Egresos
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Saldo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData[selectedReport].map((item: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatArgentinaDate(item.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(item.inflow)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-red-600 dark:text-red-400">
                    {formatCurrency(item.outflow)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-right ${
                    item.balance >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(item.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'express-metrics':
        // Vista mejorada y visual SOLO con cuadros, mostrando nombre, color y talle
        // Agrupación por combinación específica
        const periodExpress = express.filter(exp => new Date(exp.created_at) >= new Date(startDate) && new Date(exp.created_at) <= new Date(endDate));
        const periodExpressSamples = expressSamples.filter(exp => new Date(exp.created_at) >= new Date(startDate) && new Date(exp.created_at) <= new Date(endDate));
        const periodExpressItems = expressItems.filter(item => periodExpress.some(exp => exp.id === item.express_id));
        const periodExpressSampleItems = expressSampleItems.filter(item => periodExpressSamples.some(exp => exp.id === item.express_sample_id));
        const allExpressSoldItems = [...periodExpressItems, ...periodExpressSampleItems];

        // Agrupar por combinación única de producto, color y talle
        const ventasPorCombinacion: Record<string, { name: string, color: string, size: string, quantity: number }> = {};
        allExpressSoldItems.forEach(item => {
          const key = `${item.product_type}||${item.color}||${item.size}`;
          if (!ventasPorCombinacion[key]) {
            ventasPorCombinacion[key] = {
              name: item.product_type,
              color: item.color,
              size: item.size,
              quantity: 0
            };
          }
          ventasPorCombinacion[key].quantity += item.quantity;
        });
        const allItemsSorted = Object.values(ventasPorCombinacion).sort((a, b) => b.quantity - a.quantity);
        const topItem = allItemsSorted[0];

        // Top 5 remeras y top 5 buzos
        const topRemeras = allItemsSorted.filter(i => i.name && i.name.toLowerCase().includes('remera')).slice(0, 5);
        const topBuzos = allItemsSorted.filter(i => i.name && i.name.toLowerCase().includes('buzo')).slice(0, 5);

        // Totales por tipo
        const totalRemeras = allItemsSorted.filter(i => i.name && i.name.toLowerCase().includes('remera')).reduce((acc, i) => acc + i.quantity, 0);
        const totalBuzos = allItemsSorted.filter(i => i.name && i.name.toLowerCase().includes('buzo')).reduce((acc, i) => acc + i.quantity, 0);

        return (
          <div className="space-y-8">
            {/* KPIs principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader title="Pedidos Express" />
                <CardContent>
                  <p className="text-3xl font-bold text-indigo-600">{totals.totalOrders}</p>
                  <p className="text-gray-500">Total de pedidos Express</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader title="Muestras Express" />
                <CardContent>
                  <p className="text-3xl font-bold text-indigo-600">{totals.totalSamples}</p>
                  <p className="text-gray-500">Total de muestras Express</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader title="Ingresos Express" />
                <CardContent>
                  <p className="text-3xl font-bold text-indigo-600">${totals.totalRevenue}</p>
                  <p className="text-gray-500">Ingresos totales</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader title="Ticket Promedio" />
                <CardContent>
                  <p className="text-3xl font-bold text-indigo-600">${totals.avgTicket}</p>
                  <p className="text-gray-500">Ticket promedio</p>
                </CardContent>
              </Card>
            </div>

            {/* Métricas adicionales: Ítem más vendido, Total Remeras, Total Buzos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader title="Ítem más vendido" />
                <CardContent>
                  {topItem ? (
                    <>
                      <p className="text-2xl font-bold text-blue-600">{topItem.name} - {topItem.color} - {topItem.size}</p>
                      <p className="text-gray-500">{topItem.quantity} vendidos</p>
                    </>
                  ) : (
                    <p className="text-gray-400">Sin datos</p>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader title="Total Remeras Vendidas" />
                <CardContent>
                  <p className="text-2xl font-bold text-indigo-600">{totalRemeras}</p>
                  <p className="text-lg text-gray-500">Remeras</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader title="Total Buzos Vendidos" />
                <CardContent>
                  <p className="text-2xl font-bold text-indigo-600">{totalBuzos}</p>
                  <p className="text-lg text-gray-500">Buzos</p>
                </CardContent>
              </Card>
            </div>

            {/* Top 5 Remeras y Top 5 Buzos uno al lado del otro */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader title="Top 5 Remeras más vendidas" />
                <CardContent>
                  <ol className="list-decimal ml-6 space-y-1">
                    {topRemeras.length > 0 ? topRemeras.map((item, idx) => (
                      <li key={idx} className="flex flex-col mb-2">
                        <div className="flex justify-between">
                          <span>{`${item.name} - ${item.color} - ${item.size}`}</span>
                          <span className="font-semibold">{item.quantity} u.</span>
                        </div>
                      </li>
                    )) : <p className="text-gray-400">Sin datos</p>}
                  </ol>
                </CardContent>
              </Card>
              <Card>
                <CardHeader title="Top 5 Buzos más vendidos" />
                <CardContent>
                  <ol className="list-decimal ml-6 space-y-1">
                    {topBuzos.length > 0 ? topBuzos.map((item, idx) => (
                      <li key={idx} className="flex flex-col mb-2">
                        <div className="flex justify-between">
                          <span>{`${item.name} - ${item.color} - ${item.size}`}</span>
                          <span className="font-semibold">{item.quantity} u.</span>
                        </div>
                      </li>
                    )) : <p className="text-gray-400">Sin datos</p>}
                  </ol>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-amber-500 dark:text-amber-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Tipo de reporte no válido</p>
          </div>
        );
    }
  };
  
  // Render report totals based on selected report
  const renderReportTotals = () => {
    const totals = calculateReportTotals();
    
    if (!totals) {
      return null;
    }
    
    switch (selectedReport) {
      case 'income-statement':
        return (
          <div className={`mt-4 p-4 rounded-lg ${
            totals.netCashFlow >= 0
              ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}>
            <div className="flex justify-between items-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                Flujo de Efectivo Neto
              </p>
              <p className={`text-xl font-bold ${
                totals.netCashFlow >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {formatCurrency(totals.netCashFlow)}
              </p>
            </div>
          </div>
        );
      
      case 'income-statement-results':
        return (
          <div className={`mt-4 p-4 rounded-lg ${
            totals.netProfit >= 0
              ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}>
            <div className="flex justify-between items-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                Utilidad Neta
              </p>
              <p className={`text-xl font-bold ${
                totals.netProfit >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {formatCurrency(totals.netProfit)}
              </p>
            </div>
          </div>
        );
      
      case 'accounts-receivable':
        return (
          <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            <div className="flex justify-between items-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                Total por Cobrar
              </p>
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(totals.total)}
              </p>
            </div>
          </div>
        );
      
      case 'accounts-payable':
        return (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex justify-between items-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                Total por Pagar
              </p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(totals.total)}
              </p>
            </div>
          </div>
        );
      
      case 'cash-flow-projection':
        return (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Ingresos Proyectados</p>
              <p className="mt-2 text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(totals.inflows)}
              </p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Egresos Proyectados</p>
              <p className="mt-2 text-xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(totals.outflows)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${
              totals.netFlow >= 0
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Flujo Neto</p>
              <p className={`mt-2 text-xl font-bold ${
                totals.netFlow >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {formatCurrency(totals.netFlow)}
              </p>
            </div>
          </div>
        );
      
      case 'vat-sales':
        return (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Neto</p>
              <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(totals.subtotal)}
              </p>
            </div>
            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">IVA</p>
              <p className="mt-2 text-xl font-bold text-indigo-600 dark:text-indigo-400">
                {formatCurrency(totals.tax)}
              </p>
            </div>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total</p>
              <p className="mt-2 text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(totals.total)}
              </p>
            </div>
          </div>
        );
      
      case 'vat-purchases':
        return (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Neto</p>
              <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(totals.subtotal)}
              </p>
            </div>
            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">IVA</p>
              <p className="mt-2 text-xl font-bold text-indigo-600 dark:text-indigo-400">
                {formatCurrency(totals.tax)}
              </p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total</p>
              <p className="mt-2 text-xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(totals.total)}
              </p>
            </div>
          </div>
        );
      
      case 'sales-by-client':
        return (
          <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            <div className="flex justify-between items-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                Total Ventas
              </p>
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(totals.total)}
              </p>
            </div>
          </div>
        );
      
      case 'purchases-by-supplier':
        return (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex justify-between items-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                Total Compras
              </p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(totals.total)}
              </p>
            </div>
          </div>
        );
      
      case 'bank-reconciliation':
        return (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Ingresos</p>
              <p className="mt-2 text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(totals.inflows)}
              </p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Egresos</p>
              <p className="mt-2 text-xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(totals.outflows)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${
              totals.balance >= 0
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Saldo</p>
              <p className={`mt-2 text-xl font-bold ${
                totals.balance >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {formatCurrency(totals.balance)}
              </p>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Calculate cash flow data
  const calculateCashFlowData = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Calculate cash inflows from client payments
    const clientPaymentsInflow = payments
      .filter(payment => {
        const paymentDate = new Date(payment.date);
        if (paymentDate < start || paymentDate > end || payment.voided) return false;
        
        // Check if payment is from a client
        if (payment.invoice_id) {
          const invoice = invoices.find(inv => inv.id === payment.invoice_id);
          return invoice?.entity_type === 'client';
        } else {
          // Account payment - check reference
          return payment.reference?.startsWith('client:');
        }
      })
      .reduce((sum, payment) => sum + payment.amount, 0);

    // Calculate sample sales (muestras producciones) - same logic as generateIncomeStatement
    const sampleSales = samples
      .filter(sample =>
        new Date(sample.created_at) >= start &&
        new Date(sample.created_at) <= end
      )
      .reduce((s, sample) => s + (sample.sample_value || 0), 0);

    // Calculate express sales (producciones express) - same logic as generateIncomeStatement
    const expressSales = express
      .filter(o =>
        new Date(o.created_at ?? o.date) >= start &&
        new Date(o.created_at ?? o.date) <= end
      )
      .reduce((s, o) => s + (o.express_value ?? o.total ?? 0), 0);

    // Calculate express samples (muestras express) - same logic as generateIncomeStatement
    const expressSamplesSales = expressSamples
      .filter(smpl =>
        new Date(smpl.created_at ?? smpl.date) >= start &&
        new Date(smpl.created_at ?? smpl.date) <= end
      )
      .reduce((s, smpl) => s + (smpl.sample_value ?? smpl.total ?? 0), 0);

    // Total cash inflows - same calculation as generateIncomeStatement
    const cashInflows = clientPaymentsInflow + sampleSales + expressSales + expressSamplesSales;
    
    // Calculate cash outflows from supplier payments
    const supplierPayments = payments
      .filter(payment => {
        const paymentDate = new Date(payment.date);
        if (paymentDate < start || paymentDate > end || payment.voided) return false;
        
        // Check if payment is to a supplier
        if (payment.invoice_id) {
          const invoice = invoices.find(inv => inv.id === payment.invoice_id);
          return invoice?.entity_type === 'supplier';
        } else {
          // Account payment - check reference
          return payment.reference?.startsWith('supplier:');
        }
      })
      .reduce((sum, payment) => sum + payment.amount, 0);
    
    // Calculate expense costs (direct expenses)
    const expenseCosts = expenses
      .filter(expense => {
        const expenseDate = new Date(expense.date);
        return expenseDate >= start && expenseDate <= end;
      })
      .reduce((sum, expense) => sum + expense.amount, 0);
    
    const totalOutflows = supplierPayments + expenseCosts;
    const netCashFlow = cashInflows - totalOutflows;
    const cashFlowMargin = cashInflows > 0 ? (netCashFlow / cashInflows) * 100 : 0;
    
    return {
      cashInflows,
      clientPaymentsInflow,
      sampleSales,
      expressSales,
      expressSamplesSales,
      supplierPayments,
      expenseCosts,
      totalOutflows,
      netCashFlow,
      cashFlowMargin
    };
  };
  
  const generateExpressMetrics = async () => {
    // Filter Express orders in the date range
    const periodExpress = express.filter(exp => 
      new Date(exp.created_at) >= new Date(startDate) && 
      new Date(exp.created_at) <= new Date(endDate)
    );

    // Filter Express samples in the date range
    const periodExpressSamples = expressSamples.filter(exp => 
      new Date(exp.created_at) >= new Date(startDate) && 
      new Date(exp.created_at) <= new Date(endDate)
    );

    // Get all Express items for the period
    const periodExpressItems = expressItems.filter(item => 
      periodExpress.some(exp => exp.id === item.express_id)
    );

    // Get all Express sample items for the period
    const periodExpressSampleItems = expressSampleItems.filter(item => 
      periodExpressSamples.some(exp => exp.id === item.express_sample_id)
    );

    // Calculate total metrics
    const totalExpressOrders = periodExpress.length;
    const totalExpressSamples = periodExpressSamples.length;
    const totalExpressRevenue = periodExpress.reduce((sum, exp) => sum + (exp.express_value || 0), 0);
    const totalExpressSampleRevenue = periodExpressSamples.reduce((sum, exp) => sum + (exp.sample_value || 0), 0);

    // Product type analysis
    const productTypeStats = {
      'Remera': { quantity: 0, revenue: 0 },
      'Buzo': { quantity: 0, revenue: 0 }
    };

    periodExpressItems.forEach(item => {
      if (productTypeStats[item.product_type]) {
        productTypeStats[item.product_type].quantity += item.quantity;
        // Estimate revenue based on average price
        const avgPrice = item.product_type === 'Buzo' ? 15000 : 8000;
        productTypeStats[item.product_type].revenue += item.quantity * avgPrice;
      }
    });

    // Color analysis
    const colorStats: Record<string, { quantity: number, revenue: number }> = {};
    periodExpressItems.forEach(item => {
      if (!colorStats[item.color]) {
        colorStats[item.color] = { quantity: 0, revenue: 0 };
      }
      colorStats[item.color].quantity += item.quantity;
      const avgPrice = item.product_type === 'Buzo' ? 15000 : 8000;
      colorStats[item.color].revenue += item.quantity * avgPrice;
    });

    // Size analysis
    const sizeStats: Record<string, { quantity: number, revenue: number }> = {};
    periodExpressItems.forEach(item => {
      if (!sizeStats[item.size]) {
        sizeStats[item.size] = { quantity: 0, revenue: 0 };
      }
      sizeStats[item.size].quantity += item.quantity;
      const avgPrice = item.product_type === 'Buzo' ? 15000 : 8000;
      sizeStats[item.size].revenue += item.quantity * avgPrice;
    });

    // Salesperson analysis
    const salespersonStats: Record<string, { orders: number, revenue: number }> = {};
    periodExpress.forEach(exp => {
      if (!salespersonStats[exp.salesperson]) {
        salespersonStats[exp.salesperson] = { orders: 0, revenue: 0 };
      }
      salespersonStats[exp.salesperson].orders += 1;
      salespersonStats[exp.salesperson].revenue += exp.express_value || 0;
    });

    // Status analysis
    const statusStats: Record<string, { count: number, revenue: number }> = {};
    periodExpress.forEach(exp => {
      if (!statusStats[exp.status]) {
        statusStats[exp.status] = { count: 0, revenue: 0 };
      }
      statusStats[exp.status].count += 1;
      statusStats[exp.status].revenue += exp.express_value || 0;
    });

    // Top colors by quantity
    const topColors = Object.entries(colorStats)
      .sort((a, b) => b[1].quantity - a[1].quantity)
      .slice(0, 5);

    // Top sizes by quantity
    const topSizes = Object.entries(sizeStats)
      .sort((a, b) => b[1].quantity - a[1].quantity)
      .slice(0, 4);

    // Average order value
    const avgOrderValue = totalExpressOrders > 0 ? totalExpressRevenue / totalExpressOrders : 0;

    // Return metrics
    return [
      { 
        indicator: 'Total Pedidos Express', 
        value: totalExpressOrders, 
        description: `Número total de pedidos Express en el período` 
      },
      { 
        indicator: 'Total Muestras Express', 
        value: totalExpressSamples, 
        description: `Número total de muestras Express en el período` 
      },
      { 
        indicator: 'Ingresos Express', 
        value: totalExpressRevenue, 
        description: `Ingresos totales por pedidos Express` 
      },
      { 
        indicator: 'Ingresos Muestras Express', 
        value: totalExpressSampleRevenue, 
        description: `Ingresos totales por muestras Express` 
      },
      { 
        indicator: 'Ticket Promedio', 
        value: avgOrderValue, 
        description: `Valor promedio por pedido Express` 
      },
      { 
        indicator: 'Remeras Vendidas', 
        value: productTypeStats['Remera'].quantity, 
        description: `Cantidad total de remeras vendidas` 
      },
      { 
        indicator: 'Buzos Vendidos', 
        value: productTypeStats['Buzo'].quantity, 
        description: `Cantidad total de buzos vendidos` 
      },
      { 
        indicator: 'Color Más Vendido', 
        value: topColors[0]?.[0] || 'N/A', 
        description: `Color con mayor cantidad vendida (${topColors[0]?.[1].quantity || 0} unidades)` 
      },
      { 
        indicator: 'Talle Más Vendido', 
        value: topSizes[0]?.[0] || 'N/A', 
        description: `Talle con mayor cantidad vendida (${topSizes[0]?.[1].quantity || 0} unidades)` 
      },
      { 
        indicator: 'Mejor Vendedor', 
        value: Object.entries(salespersonStats).sort((a, b) => b[1].revenue - a[1].revenue)[0]?.[0] || 'N/A', 
        description: `Vendedor con mayor facturación Express` 
      },
      { 
        indicator: 'Pedidos Completados', 
        value: statusStats['finished']?.count || 0, 
        description: `Pedidos con estado 'finished'` 
      },
      { 
        indicator: 'Pedidos en Progreso', 
        value: statusStats['in_progress']?.count || 0, 
        description: `Pedidos con estado 'in_progress'` 
      }
    ];
  };
  
  // NUEVO: Procesamiento para métricas express (cuadros, top por combinación específica)
  const expressMetricsData = selectedReport === 'express-metrics' ? reportData[selectedReport] || [] : [];
  const totals = calculateReportTotals() || {};

  // Procesar productos vendidos a partir de expressMetricsData
  const productosVendidos: { name: string, quantity: number, color?: string, size?: string }[] = [
    {
      name: 'Remera',
      quantity: expressMetricsData.find((item: any) => item.indicator === 'Remeras Vendidas')?.value || 0,
      color: expressMetricsData.find((item: any) => item.indicator === 'Color Más Vendido')?.value || '',
      size: expressMetricsData.find((item: any) => item.indicator === 'Talle Más Vendido')?.value || ''
    },
    {
      name: 'Buzo',
      quantity: expressMetricsData.find((item: any) => item.indicator === 'Buzos Vendidos')?.value || 0,
      color: expressMetricsData.find((item: any) => item.indicator === 'Color Más Vendido')?.value || '',
      size: expressMetricsData.find((item: any) => item.indicator === 'Talle Más Vendido')?.value || ''
    }
    // Puedes agregar más productos aquí si los tienes
  ].filter(p => p.quantity > 0);

  // Ranking top 5 (en este caso solo hay 2, pero soporta más)
  const topItems = productosVendidos.sort((a, b) => b.quantity - a.quantity);
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FileBarChart className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Reportes
        </h2>
      </div>
      
      {/* Date Range Selection */}
      <Card>
        <CardHeader title="Período" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Período"
              value={period}
              onChange={(value) => setPeriod(value as 'current-month' | 'previous-month' | 'custom')}
              options={[
                { value: 'current-month', label: 'Mes corriente' },
                { value: 'previous-month', label: 'Mes anterior' },
                { value: 'custom', label: 'Personalizado' },
              ]}
            />
            
            {period === 'custom' && (
              <>
                <Input
                  type="date"
                  label="Desde"
                  id="startDate"
                  name="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
                
                <Input
                  type="date"
                  label="Hasta"
                  id="endDate"
                  name="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Report Selection */}
      <Card>
        <CardHeader title="Tipo de Reporte" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <Button
              variant={selectedReport === 'income-statement' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('income-statement')}
            >
              Estado de Flujo
            </Button>
            <Button
              variant={selectedReport === 'income-statement-results' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('income-statement-results')}
            >
              Estado de Resultados
            </Button>
            <Button
              variant={selectedReport === 'accounts-receivable' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('accounts-receivable')}
            >
              Cuentas por Cobrar
            </Button>
            <Button
              variant={selectedReport === 'accounts-payable' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('accounts-payable')}
            >
              Cuentas por Pagar
            </Button>
            <Button
              variant={selectedReport === 'cash-flow-projection' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('cash-flow-projection')}
            >
              Proyección de Flujo
            </Button>
            <Button
              variant={selectedReport === 'vat-sales' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('vat-sales')}
            >
              IVA Ventas
            </Button>
            <Button
              variant={selectedReport === 'vat-purchases' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('vat-purchases')}
            >
              IVA Compras
            </Button>
            <Button
              variant={selectedReport === 'sales-by-client' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('sales-by-client')}
            >
              Ventas por Cliente
            </Button>
            <Button
              variant={selectedReport === 'purchases-by-supplier' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('purchases-by-supplier')}
            >
              Compras por Proveedor
            </Button>
            <Button
              variant={selectedReport === 'express-metrics' ? 'primary' : 'outline'}
              className="w-full justify-start"
              onClick={() => setSelectedReport('express-metrics')}
            >
              Métricas Express
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Report Content */}
      <Card>
        <CardHeader 
          title={getReportTitle(selectedReport)}
          action={
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportToPDF}
                icon={<FileDown className="h-4 w-4 mr-1" />}
              >
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportToCSV}
                icon={<FileSpreadsheet className="h-4 w-4 mr-1" />}
              >
                CSV
              </Button>
            </div>
          }
        />
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin mr-2" />
              <p className="text-gray-600 dark:text-gray-400">Generando reporte...</p>
            </div>
          ) : !reportData[selectedReport] || reportData[selectedReport].length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-amber-500 dark:text-amber-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No hay datos disponibles para el período seleccionado</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Selecciona un período y genera el reporte para ver los datos</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                {renderReportTable()}
              </div>
              {renderReportTotals()}
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Cash Flow Statement */}
      {startDate && endDate && (
        <Card>
          <CardHeader title="Estado de Flujo de Efectivo" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg ${
                calculateCashFlowData().cashInflows > 0 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
              }`}>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Ingresos de Efectivo</h4>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-green-800 dark:text-green-200">Pagos de Clientes</span>
                    <span className="font-semibold text-green-800 dark:text-green-200">{formatCurrency(calculateCashFlowData().clientPaymentsInflow)}</span>
                  </div>
                  {calculateCashFlowData().sampleSales > 0 && (
                    <div className="flex justify-between">
                      <span className="text-green-800 dark:text-green-200">Muestras Producciones</span>
                      <span className="font-semibold text-green-800 dark:text-green-200">{formatCurrency(calculateCashFlowData().sampleSales)}</span>
                    </div>
                  )}
                  {calculateCashFlowData().expressSales > 0 && (
                    <div className="flex justify-between">
                      <span className="text-green-800 dark:text-green-200">Producciones Express</span>
                      <span className="font-semibold text-green-800 dark:text-green-200">{formatCurrency(calculateCashFlowData().expressSales)}</span>
                    </div>
                  )}
                  {calculateCashFlowData().expressSamplesSales > 0 && (
                    <div className="flex justify-between">
                      <span className="text-green-800 dark:text-green-200">Muestras Express</span>
                      <span className="font-semibold text-green-800 dark:text-green-200">{formatCurrency(calculateCashFlowData().expressSamplesSales)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-1">
                    <span className="font-semibold text-gray-900 dark:text-white">Total Ingresos</span>
                    <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(calculateCashFlowData().cashInflows)}</span>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                calculateCashFlowData().totalOutflows > 0 
                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
              }`}>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Egresos de Efectivo</h4>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-red-800 dark:text-red-200">Pagos a Proveedores</span>
                    <span className="font-semibold text-red-800 dark:text-red-200">{formatCurrency(calculateCashFlowData().supplierPayments)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-800 dark:text-red-200">Gastos Directos</span>
                    <span className="font-semibold text-red-800 dark:text-red-200">{formatCurrency(calculateCashFlowData().expenseCosts)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="font-semibold text-gray-900 dark:text-white">Total Salidas</span>
                    <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(calculateCashFlowData().totalOutflows)}</span>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                calculateCashFlowData().netCashFlow >= 0 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Flujo de Efectivo Neto</h4>
                <div className="mt-2">
                  <p className={`text-2xl font-bold ${
                    calculateCashFlowData().netCashFlow >= 0 
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(calculateCashFlowData().netCashFlow)}
                  </p>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                calculateCashFlowData().cashFlowMargin >= 0 
                  ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Margen de Flujo</h4>
                <div className="mt-2">
                  <p className={`text-2xl font-bold ${
                    calculateCashFlowData().cashFlowMargin >= 0 
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {calculateCashFlowData().cashFlowMargin.toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />

      {/* Express Cost Configuration Modal */}
      {showExpressCostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Configurar Costos Express
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Costo por Buzo
                </label>
                <input
                  type="number"
                  value={expressCostConfig.hoodieCost}
                  onChange={(e) => setExpressCostConfig(prev => ({
                    ...prev,
                    hoodieCost: Number(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="5000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Costo por Remera
                </label>
                <input
                  type="number"
                  value={expressCostConfig.tshirtCost}
                  onChange={(e) => setExpressCostConfig(prev => ({
                    ...prev,
                    tshirtCost: Number(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="3000"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowExpressCostModal(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowExpressCostModal(false);
                  generateReport(); // Regenerar reporte con nuevos costos
                }}
              >
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;